import { useMemo } from 'react';
import gettext from '@sdk/lang/gettext';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import Text14 from '@sdk/components/ui/typography/Text14';
import Row from '@sdk/components/ui/spacing/Row';
import HorizontalSeparator from '@sdk/components/ui/HorizontalSeparator';
import moneyDisplay from '@sdk/common/currency/moneyDisplay';
import dateToHumanFormat from '@sdk/utils/dateFormat/dateToHumanFormat';
import type { CurrencyType } from '@sdk/common/currency/types/CurrencyType';
import type { OrgSettings } from '@sdk/authentication/types/OrganisationType';
import ToastComponent from '@sdk/common/toast/ToastComponent';

import { PricingSummaryRow } from '@web/account/billing/CurrentBillingPricing/PricingSummaryRow/PricingSummaryRow';
import { SubscriptionUtilsType } from '@web/account/types';
import { ANNUAL_DISCOUNT_PERCENTAGE } from '@web/account/constants';

import { useCurrentBillingPricing } from './currentBillingPricing.hooks';
import { PricingContainer, PricingSummaryText } from './currentBillingPricing.styles';

export type Props = {
  level: number;
  isYearlyPlan: boolean;
  subscriptionUtils: SubscriptionUtilsType;
  orgSettings: OrgSettings;
  currency: CurrencyType;
  ignoreNextCharge?: boolean;
  hideNextChargeDate?: boolean;
  onTaxChange?: (taxAmount: number) => void;
};

const CurrentBillingPricing = (props: Props) => {
  const { subscriptionUtils, level, isYearlyPlan, currency, hideNextChargeDate = false } = props;
  const { billingInfo, subtotalBreakdownRows, total, yearlyDiscount } =
    useCurrentBillingPricing(props);
  const pricePerUserPerMonth = useMemo(
    () => subscriptionUtils.getPricePerUnit(level, isYearlyPlan) / (isYearlyPlan ? 12 : 1),
    [subscriptionUtils, level, isYearlyPlan]
  );
  const amount = billingInfo.quantity * pricePerUserPerMonth * (isYearlyPlan ? 12 : 1);

  return (
    <PricingContainer>
      <PricingSummaryRow label={gettext('Number of users')} value={billingInfo.quantity} />
      <Row alignItems="center">
        <PricingSummaryText>{gettext('Price per user')}</PricingSummaryText>
        {Boolean(yearlyDiscount) && isYearlyPlan && (
          <>
            <PricingSummaryText>&nbsp;(</PricingSummaryText>
            <Text14 themeColor={ThemeColorType.success}>
              {gettext('%(yearlyDiscountPercent)s% discount', {
                yearlyDiscountPercent: ANNUAL_DISCOUNT_PERCENTAGE,
              })}
            </Text14>
            <PricingSummaryText>)</PricingSummaryText>
          </>
        )}
        <Row mLeft={'auto'}>
          <PricingSummaryText>
            {moneyDisplay({ value: pricePerUserPerMonth, currency })}
          </PricingSummaryText>
        </Row>
      </Row>

      {isYearlyPlan && <PricingSummaryRow label={gettext('Annual billing')} value="x 12" />}
      {amount && (
        <PricingSummaryRow
          label={gettext('Amount')}
          value={moneyDisplay({ value: amount, currency })}
        />
      )}

      <HorizontalSeparator />

      {subtotalBreakdownRows.map(({ label, value }) => (
        <PricingSummaryRow key={label} label={label} value={moneyDisplay({ value, currency })} />
      ))}

      {Boolean(subtotalBreakdownRows.length) && <HorizontalSeparator />}
      <PricingSummaryRow
        label={gettext('Total')}
        value={moneyDisplay({ value: total, currency })}
        semiBold
      />
      {!hideNextChargeDate && (
        <PricingSummaryRow
          label={gettext('Next payment')}
          value={dateToHumanFormat(billingInfo.nextChargeDate || billingInfo.scheduledToResumeDate)}
          semiBold
        />
      )}
      <ToastComponent />
    </PricingContainer>
  );
};

export default CurrentBillingPricing;
